/**
 * The live status socket — the kiosk's half [03-us-kad-6j-b; decisions 0354, 0348].
 *
 * One `WebSocket` to `iss-kiosk-api`'s gateway, `GET /socket` (Upgrade) — the path is [KAD-6j-a]'s handover
 * contract (`LIVE_STATUS_PATH`). What it is for, in two lines: the admin reads a tablet that went away from
 * this socket CLOSING. ⚠️ A venue's publish still arrives as a `config.published` frame, and since
 * [03-us-kad-6e] nothing in the app listens for it: the *nothing published* state it recovered is gone.
 *
 * ## ⛔ It names nothing [AC-1]
 *
 * The browser sends the kiosk's `HttpOnly` session cookie with the upgrade, as it does with every other
 * credentialed call [0206], and **that cookie is the whole identity**. No `kioskId`, venue or name goes in
 * the URL and no sub-protocol is offered. There is nothing on this device to name a venue with anyway: the
 * api never gives the tablet a venue id [0204].
 *
 * ## It sends two frames and hears one [6j-b AC-5 as amended by 03-us-kad-6j-f; 0319 § Ruling 3]
 *
 * ⚠️ **6j-b's *"it sends no application frame"* is amended** — as 0319 amends 0354 § 2 — to exactly two:
 * `session.started {candidate}` and `session.ended {}`, the kiosk reporting its candidate session
 * [KAD-6j-f; the gateway's half is KAD-6j-e]. ⛔ Neither names a kiosk, a venue or a session, and the
 * candidate is 0216's `{firstName, lastInitial}` or `null`. **The one `send` in `src/` is in this file**,
 * behind {@link LiveChannel.setCandidateSession}. `config.published` is still RECOGNISED, and since
 * [03-us-kad-6e] nothing acts on it; any other type, and anything that is not a JSON `{type}` object, is
 * ignored.
 *
 * ⭐ **The invariant** [KAD-6j-f]: while a candidate session is open and a socket is open, the last kiosk
 * frame on that socket is `session.started`; once the session has ended, the last frame is `session.ended`
 * or the socket has closed. ⛔ Nothing is sent on a socket that is not open, and no frame is queued — the
 * channel remembers whether a session is open and whether an END IS OWED, and the next open says both.
 *
 * ⭐ **An end it could not send is owed** [03-us-kad-6j-h; 0437 clause 6 as amended by 0449 clause 2, amending
 * KAD-6j-f AC-3 and AC-4]: whenever the server may hold a visit the kiosk has left — after a return to Welcome
 * it could not report, at every start-up, and after any end whose socket closed before a `session.started`
 * followed it — the next frame on any open socket is `session.ended`, and no `session.started` goes out before
 * it. It is a mark, not a frame kept for later (see {@link LiveChannel}).
 *
 * ## Reconnect [AC-3; 0354 § 5]
 *
 * A socket the kiosk did not close itself is reopened after {@link reconnectDelayMs} — exponential, 1 s to
 * 30 s, full jitter — and **every reopen is a new `WebSocket`, a fresh handshake** [KAD-6j AC-3]. ⭐ **Every
 * close cause takes the same path**, including a refused upgrade (a 401 before the `101`, which a browser
 * sees only as `1006`) and `4001` (the credential ended): see {@link LiveChannel} for why neither stops it.
 * ⛔ **None of them changes anything on screen** — a removed tablet learns at its next scoped call or reload,
 * as it already does [0348].
 *
 * ## Why one closure [react-conventions § 4]
 *
 * This file is past the ~200 lines that invite a split, and stays whole on purpose: the owed-end ledger
 * (`endOwed`, `owedSince`, `unsettledOn`) reads the socket's state (`socket`, `admitted`, `refused`), and only
 * `sendFrame`'s result settles it, so it stays in {@link createLiveChannel}'s closure beside them.
 */
import { reconnectDelayMs } from '@/lib/reconnectBackoff';
import type { SessionCandidate } from '@/lib/sessionCandidate';

import {
  CONFIG_PUBLISHED,
  KIOSK_FRAME_REFUSED,
  LIVE_SOCKET_PATH,
  SESSION_ENDED,
  SESSION_STARTED,
} from './liveSocket.contract';

/** The code the kiosk closes with when it leaves the front: an ordinary, intended close. */
const NORMAL_CLOSURE = 1000;

/**
 * How long a handshake may stay unanswered before it counts as a failed attempt — the app's standing 10 s
 * request bound [`enrolment.ts`; `src/services/CLAUDE.md`: *every call has a timeout*]. ⛔ Without it a
 * handshake a branch proxy swallowed would hold the channel in *connecting* for as long as the browser
 * cares to wait, and no reconnect could start behind it.
 */
const HANDSHAKE_TIMEOUT_MS = 10_000;

/**
 * How long a socket must STAY open before the backoff starts over [review 1 item 4].
 *
 * ⛔ **Not at the `101`.** A gateway that admits and then drops at once — a credential re-check that ends it,
 * an instance that keeps dying — would otherwise reset the exponent on every admission and reconnect every
 * second forever, from every tablet at the branch. Thirty seconds is the backoff's own ceiling: an open that
 * outlives the longest wait is one the backoff no longer needs to remember.
 */
export const STABLE_OPEN_MS = 30_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/**
 * `wss://<api host>/socket` — or, in development where `VITE_API_BASE_URL` is empty, this page's own host,
 * which Vite's proxy forwards to the api exactly as it does every HTTP path.
 *
 * ⛔ **No query string, no fragment, no path segment beyond the endpoint's** [AC-1].
 */
export function liveSocketUrl(
  base: string = apiBaseUrl(),
  page: string = window.location.href,
): string {
  const url = new URL(`${base}${LIVE_SOCKET_PATH}`, page);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.search = '';
  url.hash = '';
  return url.toString();
}

/** Whether a frame is the one this kiosk acts on. Anything unreadable is simply not it. */
export function isConfigPublished(data: unknown): boolean {
  if (typeof data !== 'string') return false;
  let frame: unknown;
  try {
    frame = JSON.parse(data);
  } catch {
    return false;
  }
  return (
    typeof frame === 'object' &&
    frame !== null &&
    (frame as { readonly type?: unknown }).type === CONFIG_PUBLISHED
  );
}

/**
 * A candidate session as the channel is told about it: open, with its latest candidate (`null` until a name
 * is entered). `undefined` in its place means no session is open.
 */
export interface CandidateSessionReport {
  readonly candidate: SessionCandidate | null;
}

/** The two frames a kiosk may send — KAD-6j-e's shapes, exactly. */
type KioskFrame =
  | {
      readonly type: typeof SESSION_STARTED;
      readonly data: { readonly candidate: SessionCandidate | null };
    }
  | { readonly type: typeof SESSION_ENDED; readonly data: Record<string, never> };

function sessionStarted({ candidate }: CandidateSessionReport): KioskFrame {
  return {
    type: SESSION_STARTED,
    data: {
      // Rebuilt field by field, so nothing a caller's object carries beside the two can reach the wire.
      candidate:
        candidate === null
          ? null
          : { firstName: candidate.firstName, lastInitial: candidate.lastInitial },
    },
  };
}

const SESSION_ENDED_FRAME: KioskFrame = { type: SESSION_ENDED, data: {} };

export interface LiveChannelOptions {
  /**
   * Called once per `config.published` frame. ⛔ It carries nothing: the frame's `venueId` is not read.
   *
   * ⚠️ **Optional, and the app passes none** [03-us-kad-6e; 0429 R1]: the *nothing published* state it
   * recovered is gone. The frame is still parsed because it is [KAD-6j-a]'s contract, not this app's.
   */
  readonly onConfigPublished?: () => void;
  readonly url?: string;
  readonly random?: () => number;
}

/**
 * The channel's lifecycle, owned by whoever decides when the tablet is "in front".
 *
 * * {@link LiveChannel.open} — the tab is visible and focused: hold a socket. Idempotent: while one is
 *   connecting or open, it does nothing, so a `visibilitychange` and a `focus` arriving together for the
 *   same return make ONE handshake, not two.
 * * {@link LiveChannel.close} — the tab was hidden or blurred, or the tablet left the enrolled states:
 *   close the socket (the close IS the signal [0354 § 3]) and cancel any reconnect that was waiting.
 *   ⛔ Nothing is sent first.
 *
 * ⭐ **Why a `4001` or a refused upgrade does not stop reconnecting.** [AC-3] rules it in terms: *"a refused
 * upgrade (401) is retried by the same backoff and changes nothing on screen"*, and a reconnect after `4001`
 * IS a refused upgrade. The invariant's failure posture says the same: *"stay closed and keep backing off —
 * never send a frame to explain"*. ⭐ **The 30 s ceiling is what bounds the cost** — a removed tablet left in
 * front costs its venue one refused upgrade per 15 s on average, answered before the `101` — until its next
 * scoped call or reload takes it to the pad [0348], where this channel is closed and not reopened.
 *
 * * {@link LiveChannel.setCandidateSession} — the candidate session changed [KAD-6j-f]: one opened
 *   (`session.started`), its candidate was entered (`session.started` again), or it ended
 *   (`session.ended`). ⛔ Sent only on a socket that is open; otherwise only remembered, and every
 *   successful open while a session is open sends `session.started` first [AC-3] when no end is owed —
 *   right behind the owed end when one is. A session that ends while no socket is open sends nothing THEN
 *   [AC-4's first half] — and leaves an end owed [03-us-kad-6j-h AC-1].
 *   ⚠️ **The de-duplication compares the candidate BY REFERENCE**: telling the channel the same object again
 *   sends nothing, and a new object — a new submit, even of the same name — sends `session.started` again,
 *   which is the one-frame-per-entry the handover asks for. The store hands it a new object per entry.
 *   ⛔ **After a `1008` the channel reports nothing more for THAT session** (see {@link REFUSED_LATCH}): it
 *   keeps reconnecting, because the socket is still the tablet's presence, and the next session to open
 *   tries again — ⭐ the owed end first, if one is owed by then [03-us-kad-6j-h AC-4].
 * * **The owed end** [03-us-kad-6j-h]: one mark per channel, ⭐ SET when the channel is created [AC-2] — a
 *   new channel cannot know whether the page before it sent its end, and device storage may hold nothing
 *   that would tell it [KAD-6c AC-11] — set again by an end that could not go out on an open, admitted,
 *   unlatched socket [AC-1], and set again when the socket an end went out on closes before a
 *   `session.started` followed it (the next bullet). Every successful open sends it FIRST, before the re-send
 *   [AC-3]; every `session.started` waits behind it [AC-4]. ⛔ A mark, never a count: two ends owed are ONE
 *   frame.
 *   ⛔ Memory only — a reload starts owed instead of reading anything back.
 *   ⭐ An end a FAILED send left behind logs one line when it finally goes out ({@link reportOwedEndSent});
 *   the start-up end alone logs nothing, and so does an end owed again.
 * * **An end settles only when a `session.started` follows it on its socket** [0449 clause 2; AC-1, AC-3]:
 *   ANY end handed to a socket — the start-up end, an owed end, or one sent at once when a session ends — is
 *   only handed over, because an API already closing that socket drops it unseen while `readyState` still
 *   reads OPEN. A `session.started` sent on the same socket after it settles it; if the socket closes first, by
 *   ANY close — the API's or the network's (`onclose`), or the kiosk's own on a hide, a blur or `online`
 *   ({@link LiveChannel.close}, which reaches no `onclose`) — the end is owed again. ⛔ A socket where a
 *   `session.started` followed the end owes nothing: an end on the next open would end the visit that started.
 */
export interface LiveChannel {
  readonly open: () => void;
  readonly close: () => void;
  readonly setCandidateSession: (session: CandidateSessionReport | undefined) => void;
}

export function createLiveChannel({
  onConfigPublished,
  url = liveSocketUrl(),
  random = Math.random,
}: LiveChannelOptions): LiveChannel {
  let socket: WebSocket | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let handshake: ReturnType<typeof setTimeout> | undefined;
  let stable: ReturnType<typeof setTimeout> | undefined;
  /** Attempts that have failed since a socket last stayed open {@link STABLE_OPEN_MS} — the exponent. */
  let failures = 0;
  /** Whether {@link socket} is one the gateway ADMITTED — the only socket a frame may go out on. */
  let admitted = false;
  /** The candidate session, as last told: `undefined` when none is open. ⛔ Nothing else is kept. */
  let session: CandidateSessionReport | undefined;
  /** Set by a `1008` close, until the next session opens — see {@link REFUSED_LATCH}. */
  let refused = false;
  /**
   * ⭐ **An end is owed** [03-us-kad-6j-h] — see {@link LiveChannel}. Starts SET [AC-2]: whatever page ran
   * before this channel could not send its end. ⛔ Memory only [KAD-6c AC-11; KAD-6j-h AC-5].
   */
  let endOwed = true;
  /**
   * When an end first became owed because its send FAILED (`performance.now()`, monotonic), for the one line
   * its payment logs [review round 1, RL-2]. `undefined` while the only end owed is the start-up end, which
   * says nothing about this page, or an end owed again by a close, which did go out (see
   * {@link oweAgainIfUnsettled}). ⛔ A time, never anything about the candidate.
   */
  let owedSince: number | undefined;
  /**
   * ⭐ The socket that carried the last `session.ended`, while no `session.started` has been sent on it since
   * [0449 clause 2] — see {@link LiveChannel}. Its close owes that end again; a start sent on it settles it.
   * ⛔ Always the current socket or `undefined`: every ADMITTED socket this channel lets go of passes through
   * {@link oweAgainIfUnsettled} first (a handshake abandoned on its timeout was never admitted, so nothing went
   * out on it).
   */
  let unsettledOn: WebSocket | undefined;

  /** Whether the frame went out — on an open, admitted, unlatched socket, and nowhere else. */
  const sendFrame = (frame: KioskFrame): boolean => {
    // ⛔ Not open, not sent — and not queued: the next open reports the session as it then stands [AC-4].
    if (refused || !admitted || socket?.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(frame));
    return true;
  };

  /**
   * Sends the owed end if the socket can take it, and says whether the way is now clear: ⛔ no
   * `session.started` goes out while an end is still owed [03-us-kad-6j-h AC-3, AC-4].
   */
  const payOwedEnd = (): boolean => {
    if (endOwed && sendFrame(SESSION_ENDED_FRAME)) {
      endOwed = false;
      // ⭐ Handed over, not settled [0449 clause 2]: every end — the start-up end, an owed end, one sent at
      // once — passes here, and stays unsettled on this socket until a `session.started` follows it.
      unsettledOn = socket;
      if (owedSince !== undefined) reportOwedEndSent(performance.now() - owedSince);
      owedSince = undefined;
    }
    return !endOwed;
  };

  /**
   * Every `session.started` — the open's re-send, an opening, a name re-send — goes through here [AC-4]. ⭐ One
   * that goes out settles the end before it on this socket [0449 clause 2].
   */
  const sendStarted = (report: CandidateSessionReport): void => {
    if (payOwedEnd() && sendFrame(sessionStarted(report))) unsettledOn = undefined;
  };

  /**
   * ⭐ **A socket's close owes again an end it carried that no `session.started` followed** [0449 clause 2] —
   * any close: called from `onclose` and from {@link LiveChannel.close}. ⛔ Still a mark: an end already owed
   * and this one are one frame. ⛔ Not timed: the owed-end line is for a FAILED send only.
   */
  const oweAgainIfUnsettled = (closed: WebSocket): void => {
    if (unsettledOn !== closed) return;
    unsettledOn = undefined;
    endOwed = true;
  };

  const cancelRetry = (): void => {
    clearTimeout(retry);
    retry = undefined;
  };

  const scheduleRetry = (code: number | undefined, reason = ''): void => {
    const delay = reconnectDelayMs(failures, random);
    failures += 1;
    reportClosed(code, reason, delay);
    retry = setTimeout(() => {
      retry = undefined;
      connect();
    }, delay);
  };

  const connect = (): void => {
    let next: WebSocket;
    try {
      // ⛔ No second argument: no sub-protocol is offered, so nothing travels in `Sec-WebSocket-Protocol`.
      next = new WebSocket(url);
    } catch {
      // A constructor that throws (a malformed URL, an older engine's CSP refusal) is one more failed attempt.
      scheduleRetry(undefined);
      return;
    }
    socket = next;
    admitted = false;
    handshake = setTimeout(() => {
      // Still connecting: let it go, and treat it as the failed attempt it is. Nothing was sent on a socket
      // that never opened, so no end is unsettled on it [0449 clause 2].
      socket = undefined;
      next.close();
      scheduleRetry(undefined);
    }, HANDSHAKE_TIMEOUT_MS);

    next.onopen = () => {
      // An engine that reports an open for a handshake this channel already abandoned is ignored.
      if (socket !== next) return;
      clearTimeout(handshake);
      stable = setTimeout(() => {
        failures = 0;
      }, STABLE_OPEN_MS);
      admitted = true;
      // ⭐ Before anything else on the new socket: the owed end [KAD-6j-h AC-3], then the re-send
      // [KAD-6j-f AC-3], because the close of the last socket deleted the record.
      payOwedEnd();
      if (session !== undefined) sendStarted(session);
    };
    next.onmessage = (event: MessageEvent) => {
      if (socket !== next) return;
      if (isConfigPublished(event.data)) onConfigPublished?.();
    };
    next.onclose = (event: CloseEvent) => {
      // A socket this channel already let go of — closed on a hide or a blur — reports nothing.
      if (socket !== next) return;
      socket = undefined;
      admitted = false;
      clearTimeout(handshake);
      clearTimeout(stable);
      // ⭐ The API, the network or a deploy's `1001` closed it: an end it carried with no start after is owed.
      oweAgainIfUnsettled(next);
      if (event.code === KIOSK_FRAME_REFUSED && !refused) {
        refused = true;
        console.error({ msg: REFUSED_LATCH, alert: 'live_socket_session_reporting_stopped' });
      }
      // ⭐ Only a close the kiosk did NOT ask for arrives here: its own closes let go of the socket first.
      scheduleRetry(event.code, event.reason);
    };
  };

  return {
    open: () => {
      if (socket !== undefined) return;
      // A return to the front does not wait out a backoff: it is a fresh handshake now [AC-3].
      cancelRetry();
      connect();
    },
    close: () => {
      cancelRetry();
      clearTimeout(handshake);
      clearTimeout(stable);
      const held = socket;
      socket = undefined;
      admitted = false;
      if (held !== undefined) {
        // ⭐ The kiosk's own close is a close too [0449 clause 2], and it reaches no `onclose` here.
        oweAgainIfUnsettled(held);
        // ⛔ No `session.ended` first [KAD-6j-f AC-5]: a hide or a blur is not the end of the session.
        held.close(NORMAL_CLOSURE);
      }
    },
    setCandidateSession: (next) => {
      const previous = session;
      session = next;
      // A NEW session tries again [review 2 item B]: the latch stops one session's re-send loop, never
      // every later candidate on this tablet.
      if (previous === undefined && next !== undefined) refused = false;
      if (next === undefined) {
        // ⭐ An end is owed until a socket takes it [KAD-6j-h AC-1]: sent now if it can be, else first on the
        // next open — and one sent now is unsettled like any other [0449 clause 2]. ⛔ The mark is set, not
        // counted — an end already owed and this one are one frame.
        if (previous !== undefined) {
          endOwed = true;
          // A FAILED send is timed for the line its payment logs; the earliest one still unpaid counts.
          if (!payOwedEnd()) owedSince ??= performance.now();
        }
        return;
      }
      // A session opening, or its candidate entered again: either way the record is overwritten [6j-e AC-1].
      if (previous?.candidate !== next.candidate) {
        sendStarted(next);
      }
    },
  };
}

/**
 * ⭐ **A `1008` stops the candidate-session report until the next session opens** [PR 250 review 1 item 1,
 * scoped by review 2 item B].
 *
 * The gateway refuses a session frame when it predates KAD-6j-e (an api without the two kiosk frames closes
 * any frame `1008`) or when the two sides' bounds have drifted. Either way the same `session.started` would be
 * refused again on every reconnect, for as long as the session stays open — a reconnect storm with one
 * refused frame each. So the channel stops reporting that session and says so ONCE; it keeps reconnecting,
 * because the socket-as-presence still works. The next session to open clears it:
 * at most one refused frame per session start, and a tablet whose api has caught up reports again with no
 * reload.
 */
export const REFUSED_LATCH =
  'the gateway refused a frame this kiosk sent (1008) — this candidate session is no longer reported; the next one will try again';

/**
 * ⭐ **An end that had to wait leaves one trace** [review round 1, RL-2]: a return to Welcome the kiosk could not
 * report at once — the socket was down, not yet admitted, closing, or latched — reached the server only later,
 * on the next open or with the next session's start. Until then the server held a visit the kiosk had left.
 * `warn`, as for any close the kiosk did not ask for: an operating condition, not a defect. `owedForMs` says
 * how long. ⛔ Not for the start-up end (every load owes one). ⛔ Nothing identifying: no candidate, no
 * `kioskId`, no venue.
 */
function reportOwedEndSent(owedForMs: number): void {
  console.warn({
    msg: 'the live socket sent a session.ended this kiosk could not send when its visit ended',
    alert: 'live_socket_owed_end_sent',
    owedForMs: Math.round(owedForMs),
  });
}

/** The longest close reason logged. The gateway's reasons are its own constant strings, well under this. */
const MAX_LOGGED_REASON = 64;

/**
 * ⭐ **A close the kiosk did not ask for leaves a trace**, because nothing on screen will [0348].
 *
 * `warn` for the ordinary causes — a network drop, a deploy, a refused upgrade, a removed device, a
 * candidate session the server could not record (`1011`) — and `error` for `1008`, which only happens if
 * this client sent a frame the gateway refused: one off the kiosk's list, or, since KAD-6j-e, a
 * `session.started` / `session.ended` it judged invalid. Either is a defect in this client, never an
 * operating condition. ⛔ Nothing identifying: no `kioskId`, no venue, no cookie, no name.
 */
function reportClosed(code: number | undefined, rawReason: string, retryInMs: number): void {
  /*
   * The reason is the server's own constant (`candidate_session_unrecorded`, `kiosk_frame_invalid`, …), so it
   * tells a `1011` from a network drop. Capped all the same: it is text from the far side.
   */
  const reason = rawReason.slice(0, MAX_LOGGED_REASON);
  if (code === KIOSK_FRAME_REFUSED) {
    console.error({
      msg: 'the gateway closed the live socket because it refused a frame this kiosk sent — a type off the kiosk list, or a session frame it judged invalid',
      alert: 'live_socket_kiosk_sent_a_frame',
      code,
      reason,
      retryInMs,
    });
    return;
  }
  console.warn({
    msg: 'the live status socket closed while the kiosk was in front — reopening after a backoff',
    alert: 'live_socket_closed',
    code,
    reason,
    retryInMs,
  });
}
